import { useState } from 'react'

import copy from '../../demo/fixtures/w5/copy.json'
import { useDemoStore } from '../../demo/store'
import { Button, GlossaryText, Modal, Toast, ToastHost } from '../../ui'
import { ProcessSteps } from './ProcessSteps'
import { fillTemplate, selectAlerts, type W5Live } from './live'
import { useProcessRun } from './useProcessRun'
import styles from './AnalysisPage.module.css'

interface MonitorRulesDialogProps {
  open: boolean
  rules: W5Live['monitorRules']
  onClose: () => void
}

export function MonitorRulesDialog({ open, rules, onClose }: MonitorRulesDialogProps) {
  const setMonitorRule = useDemoStore((state) => state.setMonitorRule)
  const [toast, setToast] = useState<string | null>(null)
  const { visibleSteps, running, run } = useProcessRun()

  const toggle = async (rule: W5Live['monitorRules'][number]) => {
    if (running) return
    const nextOn = !rule.on
    const before = useDemoStore.getState()
    const beforeAlerts = selectAlerts(before.campaigns, before.ads, before.w5).filter((alert) => alert.tone !== 'ok')
    setMonitorRule(rule.key, nextOn)
    const after = useDemoStore.getState()
    const afterAlerts = selectAlerts(after.campaigns, after.ads, after.w5).filter((alert) => alert.tone !== 'ok')
    const ruleAlerts = (nextOn ? afterAlerts : beforeAlerts).filter((alert) => alert.ruleKey === rule.key)
    const vars = {
      key: rule.key,
      cond: rule.cond,
      window: rule.window,
      level: rule.level,
      hits: ruleAlerts.length,
      objects: ruleAlerts.map((alert) => alert.object).join('、') || '暂无命中',
      alertsBefore: beforeAlerts.length,
      alertsAfter: afterAlerts.length,
    }
    const templates = nextOn ? copy.process.monitorRuleOn : copy.process.monitorRuleOff
    const completed = await run(templates.map((step) => ({ label: fillTemplate(step.label, vars), detail: fillTemplate(step.detail, vars) })), copy.process)
    if (!completed) return
    setToast(nextOn
      ? `已开启监控规则 ${rule.key}（${rule.cond}），重新纳入 ${ruleAlerts.length} 项告警`
      : `已关闭监控规则 ${rule.key}（${rule.cond}），当前告警 ${beforeAlerts.length} → ${afterAlerts.length} 项`)
  }

  return (
    <Modal open={open} title="监控规则" hint="关闭规则后，对应告警与任务监控状态会立即更新" onClose={running ? () => undefined : onClose} footer={<Button disabled={running} onClick={onClose}>完成</Button>}>
      <div className={styles.ruleDialogList}>
        {rules.map((rule) => (
          <div className={styles.ruleDialogRow} key={rule.key}>
            <div><p><b>{rule.key}</b> · <GlossaryText text={rule.cond} /></p><span>{rule.window} · {rule.level}优先级 · 当前命中 {rule.hits} 项</span></div>
            <Button aria-checked={rule.on} aria-label={`${rule.cond}，${rule.on ? '已开启' : '已关闭'}`} className={`${styles.switchButton} ${rule.on ? styles.switchOn : ''}`} disabled={running} role="switch" variant="ghost" onClick={() => void toggle(rule)}><i /></Button>
          </div>
        ))}
      </div>
      {running ? <div className={styles.processSlot}><ProcessSteps steps={visibleSteps} /></div> : null}
      {toast ? <ToastHost inline><Toast onDismiss={() => setToast(null)}>{toast}</Toast></ToastHost> : null}
    </Modal>
  )
}
